import { sendToParent } from "../../services/embed-service"
import { registerMessageCallbacks } from "./useEmbedMessaging"
import type { MessageOf } from "@vitable/connect/core"

/**
 * Error codes sent cross-origin via AUTH_ERROR postMessage.
 * Parent SDK consumers should handle these codes to surface appropriate UX.
 *
 * - FORBIDDEN: Retried request returned 403 — user lacks permission even with a fresh token.
 * - TOKEN_INVALID: Retried request returned 401 — refreshed token was rejected by the backend.
 * - TOKEN_REFRESH_TIMEOUT: Parent did not respond to TOKEN_EXPIRED within 30 seconds.
 * - SESSION_EXPIRED: 5 consecutive refresh failures — circuit breaker tripped, session is unrecoverable.
 */
const SAFE_ERROR_CODES = {
  FORBIDDEN: "FORBIDDEN",
  TOKEN_INVALID: "TOKEN_INVALID",
  TOKEN_REFRESH_TIMEOUT: "TOKEN_REFRESH_TIMEOUT",
  SESSION_EXPIRED: "SESSION_EXPIRED",
} as const

const REFRESH_TIMEOUT_MS = 30_000
const MAX_CONSECUTIVE_FAILURES = 5

// --- Module-level singleton state ---

let sessionToken: string | null = null
let refreshPromise: Promise<string> | null = null
let resolveRefresh: ((token: string) => void) | null = null
let consecutiveRefreshFailures = 0

// --- Public API ---

export function getToken(): string | null {
  return sessionToken
}

export function setToken(token: string): void {
  sessionToken = token
}

export function clearToken(): void {
  sessionToken = null
}

export function recordRefreshFailure(): void {
  consecutiveRefreshFailures++
}

/**
 * Requests a fresh session token from the parent frame.
 *
 * Behaviour:
 * - Returns a **shared promise** so concurrent callers coalesce into a single
 *   refresh cycle (mutex pattern).
 * - Sends `TOKEN_EXPIRED` to the parent and waits for a `TOKEN_UPDATE` reply.
 * - If the parent does not respond within 30 s the promise rejects with
 *   `TOKEN_REFRESH_TIMEOUT`.
 * - After 5 consecutive failures (timeouts **or** backend rejections of the
 *   refreshed token) the circuit breaker trips and all subsequent calls reject
 *   immediately with `SESSION_EXPIRED`.
 *
 * @param reason - Optional reason included in the TOKEN_EXPIRED payload
 *   (e.g. `"permission_denied"` for 403 responses).
 * @returns The new session token on success.
 */
export function handleTokenExpired(reason?: string): Promise<string> {
  if (consecutiveRefreshFailures >= MAX_CONSECUTIVE_FAILURES) {
    sendToParent("AUTH_ERROR", {
      code: SAFE_ERROR_CODES.SESSION_EXPIRED,
      message: "Too many consecutive refresh failures"
    })
    return Promise.reject(new Error("SESSION_EXPIRED"))
  }

  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = new Promise<string>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      consecutiveRefreshFailures++
      resolveRefresh = null
      if (consecutiveRefreshFailures >= MAX_CONSECUTIVE_FAILURES) {
        sendToParent("AUTH_ERROR", {
          code: SAFE_ERROR_CODES.SESSION_EXPIRED,
          message: "Too many consecutive refresh failures"
        })
        reject(new Error("SESSION_EXPIRED"))
        return
      }
      sendToParent("AUTH_ERROR", {
        code: SAFE_ERROR_CODES.TOKEN_REFRESH_TIMEOUT,
        message: "Token refresh timed out"
      })
      reject(new Error("Token refresh timed out"))
    }, REFRESH_TIMEOUT_MS)

    resolveRefresh = (token: string) => {
      clearTimeout(timeoutId)
      resolve(token)
    }

    sendToParent("TOKEN_EXPIRED", { reason })
  }).finally(() => {
    refreshPromise = null
    resolveRefresh = null
  })

  return refreshPromise
}

// --- Callback handlers ---

function onInit(payload: MessageOf<"VITABLE_CONNECT_INIT">["payload"]): void {
  setToken(payload.sessionToken)
  sendToParent("TOKEN_RECEIVED")
  consecutiveRefreshFailures = 0
}

function onTokenUpdate(payload: MessageOf<"TOKEN_UPDATE">["payload"]): void {
  setToken(payload.sessionToken)
  consecutiveRefreshFailures = 0

  if (resolveRefresh) {
    resolveRefresh(payload.sessionToken)
  }

  try { sendToParent("TOKEN_RECEIVED") } catch { /* notification failure is non-critical */ }
}

// --- Registration + reset ---

function _registerCallbacks(): void {
  if (typeof window === "undefined" || window.self === window.top) {
    return
  }
  registerMessageCallbacks("embedAuth", { onInit, onTokenUpdate })
}

export function __resetEmbedAuth(): void {
  sessionToken = null
  refreshPromise = null
  resolveRefresh = null
  consecutiveRefreshFailures = 0
  // Tests call this explicitly to re-register after __resetCallbackRegistry clears all.
  // Bypass the iframe guard since tests run in jsdom (window.self === window.top).
  registerMessageCallbacks("embedAuth", { onInit, onTokenUpdate })
}

// Initial registration at module load time (only in iframe context)
_registerCallbacks()
